import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs';

export interface AddressSearchResult {
  street: string;
  houseNumber: string;
  city: string;
  zip: string;
  fullAddress: string;
}

interface RuianSuggestResponse {
  suggestions: {
    text: string;
    magicKey: string;
    isCollection: boolean;
    type: string;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class AddressSearchService {
  private http = inject(HttpClient);

  /**
   * Search for addresses using Czech state RUIAN service
   * Using suggest operation for address autocomplete
   */
  searchAddresses(query: string): Observable<AddressSearchResult[]> {
    if (!query || query.length < 3) {
      return new Observable(subscriber => subscriber.next([]));
    }

    // Using Czech state RUIAN service
    const url = `https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer/exts/GeocodeSOE/suggest`;
    const params = {
      text: query,
      f: 'json',
      maxSuggestions: '10',
    };

    return this.http.get<RuianSuggestResponse>(url, { params }).pipe(
      map(response => {
        if (!response?.suggestions || !Array.isArray(response.suggestions)) {
          return [];
        }

        return response.suggestions
          .filter(suggestion => suggestion.type === 'AdresniMisto' || suggestion.type === 'Ulice')
          .map(suggestion => {
            const address = suggestion.text;
            const parts = this.parseAddress(address);

            return {
              street: parts.street,
              houseNumber: parts.houseNumber,
              city: parts.city,
              zip: parts.zip,
              fullAddress: address,
            };
          });
      })
    );
  }

  /**
   * Parse address string from RUIAN service
   * Format: "Street Name Number, City" or "Street Name Number, City ZIP"
   */
  private parseAddress(address: string): {
    street: string;
    houseNumber: string;
    city: string;
    zip: string;
  } {
    // Handle addresses like "Na Rozhledu 123, Jenišov" or "Na Rozhledu 123, Jenišov 12345"
    const commaIndex = address.indexOf(',');

    if (commaIndex > 0) {
      const streetPart = address.substring(0, commaIndex).trim();
      const cityPart = address.substring(commaIndex + 1).trim();

      // Extract house number from street part
      const { street, houseNumber } = this.parseStreetAndNumber(streetPart);

      // Try to extract ZIP from city part if present
      const zipMatch = cityPart.match(/\d{5}/);
      const zip = zipMatch ? zipMatch[0] : '';
      const city = zipMatch ? cityPart.replace(/\d{5}/, '').trim() : cityPart;

      return {
        street: street,
        houseNumber: houseNumber,
        city: city,
        zip: zip,
      };
    }

    // If no comma, treat as street only and try to extract number
    const { street, houseNumber } = this.parseStreetAndNumber(address);
    return {
      street: street,
      houseNumber: houseNumber,
      city: '',
      zip: '',
    };
  }

  /**
   * Parse street name and house number from street part
   * Handles formats like "Na Rozhledu 123", "Karlova 45/12", "Národní třída 1"
   */
  private parseStreetAndNumber(streetPart: string): { street: string; houseNumber: string } {
    // Match various house number formats:
    // - Simple number: "123"
    // - Fraction: "45/12"
    // - With letter: "123a"
    // - Complex: "45/12a"
    const houseNumberMatch = streetPart.match(/\s+(\d+(?:\/\d+)?[a-zA-Z]?)$/);

    if (houseNumberMatch) {
      const houseNumber = houseNumberMatch[1];
      const street = streetPart.substring(0, houseNumberMatch.index).trim();
      return { street, houseNumber };
    }

    // No house number found, return whole string as street
    return { street: streetPart, houseNumber: '' };
  }

  /**
   * Get search suggestions with debouncing
   */
  getSearchSuggestions(searchTerm$: Observable<string>): Observable<AddressSearchResult[]> {
    return searchTerm$.pipe(
      debounceTime(300), // Wait 300ms after user stops typing
      distinctUntilChanged(), // Only emit if value has changed
      switchMap(term => this.searchAddresses(term))
    );
  }
}
